import { debounce } from "lodash";
import IconAudioOff from "@/assets/student-class/audio-off.svg";
import IconAudioOn from "@/assets/student-class/audio-on.svg";
import IconVideoOff from "@/assets/student-class/video-off.svg";
import IconVideoOn from "@/assets/student-class/video-on.svg";
import IconCog from "@/assets/teacher-class/cog.svg";
import IconLowWifi from "@/assets/teacher-class/slow-wifi.svg";
import { TeacherState } from "@/store/room/interface";
import { generateAvatar } from "@/utils/utils";
import { CheckOutlined, LoadingOutlined } from "@ant-design/icons-vue";
import { Button, Modal, Row, Space, Spin } from "ant-design-vue";
import { computed, defineComponent, ref, watch } from "vue";
import { fmtMsg, LoginInfo } from "vue-glcommonui";
import { useStore } from "vuex";
import {
  VideoCameraIcon,
  VideoCameraSlashIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  XMarkIcon,
  Cog6ToothIcon,
  ArrowsPointingInIcon,
  WifiIcon,
} from "@heroicons/vue/20/solid";
import NameTag from "@/components/common/name-tag/name-tag.vue";
import { useAgoraVideoSpinner } from "@/hooks/use-agora-video-spinner";
import { isUserPublishedAudio, isUserPublishedVideo } from "@/store/calling/utils";
import { MediaDeviceError, getMediaDeviceErrorMessage } from "@/agora/utils";
import { vuexName, MODULES } from "@/store/utils";
import { useDeviceToggling } from "@/hooks/useDeviceToggling";
import { DynamicModalNames } from "@/store/modal/actions";
import { LowBandWidthLocales } from "@/locales/localeid";
import { useDeviceSettingModal } from "@/hooks/useDeviceSettingModal";

export default defineComponent({
  name: "TeacherClassTeacherCard",
  props: {
    teacher: {
      type: Object as () => TeacherState,
      required: true,
    },
    isMinimized: Boolean,
    onClickMinimized: {
      type: Object as () => void,
      required: true,
    },
  },
  components: {
    LoadingOutlined,
    NameTag,
    Button,
    Row,
    CheckOutlined,
    Space,
    Spin,
    Modal,
    VideoCameraIcon,
    VideoCameraSlashIcon,
    SpeakerWaveIcon,
    SpeakerXMarkIcon,
    XMarkIcon,
    Cog6ToothIcon,
    ArrowsPointingInIcon,
    WifiIcon,
  },
  setup: function (props) {
    const { getters, dispatch, commit } = useStore();
    const { toggleModal } = useDeviceSettingModal();
    const { toggleMicrophone, toggleCamera, isCameraToggling, isMicrophoneToggling } = useDeviceToggling();
    const poorConnectionText = computed(() => fmtMsg(LowBandWidthLocales.VideoLowBandWidthMessage));
    const isJoinedSession = computed<boolean>(() => getters[vuexName(MODULES.CALLING.GETTERS.GET_IS_JOINED_SESSION)]);
    const dynamicModalName = computed<string>(() => getters[vuexName(MODULES.MODAL.GETTERS.NAME)]);
    const cameraError = computed<MediaDeviceError>(() => getters[vuexName(MODULES.CALLING.GETTERS.GET_CAMERA_ERROR)]);
    const microphoneError = computed<MediaDeviceError>(() => getters[vuexName(MODULES.CALLING.GETTERS.GET_MICROPHONE_ERROR)]);
    const cameraErrorMessage = computed(() => getMediaDeviceErrorMessage(cameraError.value));
    const microphoneErrorMessage = computed(() => getMediaDeviceErrorMessage(microphoneError.value));
    const { videoRef, spinnerVisible } = useAgoraVideoSpinner();
    const isInOneToOneMode = computed(() => !!getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)]);
    const anotherTeacherIsInOneToOneMode = computed(
      () => !!getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_WITH_ANOTHER_TEACHER_ID)],
    );
    const isOneToOneWithHelperIgnoreTeacherVoice = computed(
      () => getters[vuexName(MODULES.CLASS_TEACHING.GETTERS.GET_ONE_TO_ONE_WITH_HELP_IGNORE_TEACHER_VOICE)],
    );
    const contextMenuVisibility = ref(false);
    const toggleContextMenu = () => {
      contextMenuVisibility.value = !contextMenuVisibility.value;
    };
    const hideContextMenu = () => {
      contextMenuVisibility.value = false;
    };
    const isAudioPublished = computed<boolean>(() => isUserPublishedAudio({ userId: props.teacher.id, gettersOrRootGetters: getters }));
    const isVideoPublished = computed<boolean>(() => isUserPublishedVideo({ userId: props.teacher.id, gettersOrRootGetters: getters }));
    const audioIcon = computed(() => (isAudioPublished.value ? IconAudioOn : IconAudioOff));
    const videoIcon = computed(() => (isVideoPublished.value ? IconVideoOn : IconVideoOff));

    const loginInfo: LoginInfo = getters[vuexName(MODULES.AUTH.GETTERS.GET_LOGIN_INFO)];
    const userId = loginInfo.profile.sub;

    // TODO: Combine updateMicOpen and toggleAudio into one
    const toggleAudio = async () => {
      await toggleMicrophone(async () => {
        await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_TEACHER_AUDIO), { enable: !isAudioPublished.value });
      });
    };

    // TODO: Combine updateCamOpen and toggleVideo into one
    const toggleVideo = async () => {
      await toggleCamera(async () => {
        await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_TEACHER_VIDEO), { enable: !isVideoPublished.value });
      });
    };

    const isMeHelper = computed(() => getters[vuexName(MODULES.TEACHER.GETTERS.GET_IS_HELPER)]);
    const isLowBandWidth = computed(() => getters[vuexName(MODULES.APP.GETTERS.GET_LOW_BAND_WIDTH, false)]);
    const avatarTeacher = computed(() => generateAvatar(props.teacher?.avatar));
    const isSupportedVideo = computed(() => !!(window as any).chrome && !(typeof SharedArrayBuffer === "function"));
    const oneToOneStudentId = computed<boolean>(() => getters[vuexName(MODULES.TEACHER_ROOM.GETTERS.GET_STUDENT_MODE_ONE_ID)]);
    const toggleTeacherVoiceShown = computed(() => isMeHelper.value && oneToOneStudentId.value);

    const handleClickShowDeviceModal = () => {
      toggleModal(true, {
        isMicEnabled: isAudioPublished.value,
        isCamEnabled: isVideoPublished.value,
        onUpdateMicOpen: async (micOpen: boolean) => {
          await toggleMicrophone(async () => {
            await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_TEACHER_AUDIO), {
              id: props.teacher?.id,
              enable: micOpen,
            });
          });
        },
        onUpdateCamOpen: async (camOpen: boolean) => {
          await toggleCamera(async () => {
            await dispatch(vuexName(MODULES.TEACHER_ROOM.DISPATCHES.SET_TEACHER_VIDEO), {
              id: props.teacher?.id,
              enable: camOpen,
            });
          });
        },
      });
    };

    const openDeviceWarningModal = async (isCamera: boolean) => {
      await dispatch(vuexName(MODULES.MODAL.DISPATCHES.INFORM_DEVICE_WARNING), {
        code: isCamera ? cameraError.value.code : microphoneError.value.code,
        isCamera,
        callback: isCamera ? toggleVideo : toggleAudio,
      });
    };

    watch(isVideoPublished, async (val) => {
      if (val && dynamicModalName.value === DynamicModalNames.CameraWarning) {
        commit(vuexName(MODULES.MODAL.COMMITS.CLOSE));
      }
    });

    watch(isAudioPublished, async (val) => {
      if (val && dynamicModalName.value === DynamicModalNames.MicrophoneWarning) {
        commit(vuexName(MODULES.MODAL.COMMITS.CLOSE));
      }
    });

    const toggleOneToOneWithHelperIgnoreTeacherVoice = debounce(async () => {
      await dispatch(
        vuexName(MODULES.TEACHER_ROOM.DISPATCHES.TOGGLE_ONE_TO_ONE_WITH_HELPER_IGNORE_TEACHER_VOICE),
        !isOneToOneWithHelperIgnoreTeacherVoice.value,
      );
    }, 50);
    const isTeacherCalling = computed(() => getters[vuexName(MODULES.CALLING.GETTERS.GET_IS_CALLING_HAS_USER_BY_ID)](props.teacher.id));
    const isAvatarShown = computed(() => {
      if (!isJoinedSession.value) return false;
      if (isMeHelper.value) {
        return isTeacherCalling.value && (!isVideoPublished.value || anotherTeacherIsInOneToOneMode.value);
      } else {
        return !isVideoPublished.value;
      }
    });

    return {
      audioIcon,
      videoIcon,
      toggleAudio,
      toggleVideo,
      contextMenuVisibility,
      toggleContextMenu,
      hideContextMenu,
      isLowBandWidth,
      IconLowWifi,
      IconCog,
      avatarTeacher,
      isSupportedVideo,
      userId,
      isMeHelper,
      handleClickShowDeviceModal,
      isVideoPublished,
      isAudioPublished,
      toggleTeacherVoiceShown,
      isInOneToOneMode,
      isOneToOneWithHelperIgnoreTeacherVoice,
      toggleOneToOneWithHelperIgnoreTeacherVoice,
      anotherTeacherIsInOneToOneMode,
      VideoCameraIcon,
      VideoCameraSlashIcon,
      SpeakerWaveIcon,
      SpeakerXMarkIcon,
      videoRef,
      spinnerVisible,
      isAvatarShown,
      cameraError,
      microphoneError,
      cameraErrorMessage,
      microphoneErrorMessage,
      isJoinedSession,
      isTeacherCalling,
      isMicrophoneToggling,
      isCameraToggling,
      openDeviceWarningModal,
      poorConnectionText,
    };
  },
});
